// alternative-components/approval/TxnApprovalContainer.tsx
import { useEffect, useState } from "react";
import { type GridRowClickEvent } from "@progress/kendo-react-grid";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import SuccessModalSM from "@/app/models/dialogs/SuccessModalSM";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import DialogXl from "@/app/models/dialogs/DialogXl";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import ApproveRequiredTable from "../../../Dashboard/transactions/ApproveRequired";
import { DetailsComponent } from "../../../Dashboard/PaymentDetails/DetailsComponent";
import SearchInput from "@/app/components/common/actions/inputs-b/SearchInput";
import { gxPaymentPendingViewSummaryApi } from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import { gxIsArray } from "@/app/utils/helpers";
import { Constants } from "@/app/utils/AppConstants";
import { useDebounce } from "@/app/hooks/useDebounce";
import type { ApprovalRequiredTransactions } from "@/app/types/payments";
import { useT } from "@/app/hooks/useT";
import { usePaymentApprovalFlow } from "../../types/usePaymentApprovalFlow";

interface Access {
  view: boolean;
  approve: boolean;
}

interface TxnApprovalContainerProps {
  isOpenTxnList: boolean;
  setIsOpenTxnList: (open: boolean) => void;
  access: Access;
  setCallCount: React.Dispatch<React.SetStateAction<boolean>>;

  initialTransaction?: ApprovalRequiredTransactions | null;
  initialTransactionKey?: number;
}

const TxnApprovalContainer = ({
  isOpenTxnList,
  setIsOpenTxnList,
  access,
  setCallCount,

  initialTransaction,
  initialTransactionKey,
}: TxnApprovalContainerProps) => {
  const t = useT();

  // Dashboard-parity: authzType "TRANS" - gxPaymentApproveApi + gxApprovalNonceApi
  // (nonce is called internally by selectTransaction for admin/awaiting-approval rows)
  const flow = usePaymentApprovalFlow("TRANS");

  const [tableList, setTableList] = useState<ApprovalRequiredTransactions[]>(
    [],
  );
  const [isFetching, setIsFetching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);

  const loadTxnList = async () => {
    setIsFetching(true);
    try {
      const payload = {
        pageNo: 1,
        pageSize: 20,
        sortBy: Constants.ascending,
        status: `${Constants.awaitingApproval}`,
        searchText: debouncedSearch,
      };
      const result = await gxPaymentPendingViewSummaryApi(payload);
      if (gxIsArray(result?.data)) {
        setTableList(result.data);
      } else {
        setTableList([]);
      }
    } catch (err) {
      console.log("Error in loadTxnList::", err);
      setTableList([]);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (isOpenTxnList) loadTxnList();
  }, [isOpenTxnList, debouncedSearch]);

  const handleClose = () => setIsOpenTxnList(false);

  const handleRowClick = (event: GridRowClickEvent) => {
    const row = event?.dataItem as ApprovalRequiredTransactions | undefined;
    if (row) flow.selectTransaction(row);
  };

  const handleSuccessModalClose = () => {
    flow.closeSuccessModal();
    setCallCount(true);
    loadTxnList();
  };

  // Inside the component, add:
  useEffect(() => {
    if (initialTransaction) {
      flow.selectTransaction(initialTransaction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTransactionKey]);

  return (
    <>
      <DialogXl
        title={t("approval")}
        subtitle={t("required")}
        open={isOpenTxnList}
        handleClose={handleClose}
        showFooter={false}
      >
        <div className="gx-approve-required-modal relative">
          <div className="mt-3">
            <SearchInput
              searchQuery={searchText}
              onSearchChange={setSearchText}
              placeholder={t("search_growx_ref_bene")}
            />
          </div>

          <div className="mt-3">
            {tableList.length > 0 ? (
              <ApproveRequiredTable
                onRowClick={handleRowClick}
                tableList={tableList}
              />
            ) : (
              <div className="flex justify-center py-20">
                <NoDataFound message="No Approval Found" />
              </div>
            )}
          </div>

          {isFetching && <SubmitLoading />}
        </div>
      </DialogXl>

      <DrawerNoFooter
        title={t("approval")}
        subtitle={t("request")}
        openDrawer={flow.openApprovalDrawer}
        onClose={() => flow.setOpenApprovalDrawer(false)}
      >
        <DetailsComponent
          transactionDetailsLoader={flow.transactionDetailsLoader}
          transaction={flow.transactionDetails}
          handleReject={flow.handleReject}
          handleSentForRepair={flow.handleReject}
          handleApprove={flow.handleApprove}
          hideFooter={!access?.approve}
          schedule={false}
          showEdit={false}
          fromRtp={false}
        />
      </DrawerNoFooter>

      {flow.openRejectAndNoteModal && (
        <RemarksModal
          isOpen={flow.openRejectAndNoteModal}
          onClose={flow.closeRemark}
          errors={{}}
          setValue={() => {}}
          isReject={flow.isReject}
          value={flow.remark}
          onChange={flow.setRemark}
          submit={flow.submitReject}
        />
      )}

      {flow.openSuccessModal && (
        <SuccessModalSM
          onClose={handleSuccessModalClose}
          isOpen={flow.openSuccessModal}
          successData={flow.successData}
        />
      )}

      {flow.isLoading && <SubmitLoading />}
    </>
  );
};

export default TxnApprovalContainer;
